import { ReactNode, createContext, useEffect, useState } from "react";
import { User, UserContextType } from "@/tracker-types";
import { validateSession } from "@/services/user-services";

export const UserContext = createContext<UserContextType>({
  user: null,
  setUser: () => {}, // This is a no-op function
});

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  // Start as null — user is only set after server-side session validation.
  // isValidating gates all rendering so nothing renders with stale state.
  const [user, setUser] = useState<User | null>(null);
  
  const [viewAsId, setViewAsId] = useState<number | null>(() => {
    const storedId = localStorage.getItem("viewAsUserId");
    return storedId ? Number(storedId) : null;
  });

  const [isValidating, setIsValidating] = useState(true);

  const contextValue: UserContextType = {
    user,
    setUser,
    viewAsId,
    setViewAsId,
  };

  // Validate session on mount
  useEffect(() => {
    const checkSession = async () => {
      const storedUser = localStorage.getItem("user");

      if (storedUser) {
        try {
          const sessionData = await validateSession();

          // Response is unwrapped by interceptor: { user: {...} }
          if (sessionData?.user) {
            // Defensively strip password field if backend accidentally sends it
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { password: _pw, ...serverUser } = sessionData.user as User & { password?: string };
            // Always set from server — user state starts as null so comparison is unnecessary
            setUser(serverUser);
            localStorage.setItem("user", JSON.stringify(serverUser));
          } else {
            // Session invalid, clear user data
            console.log("Session invalid, clearing user data");
            setUser(null);
            localStorage.removeItem("user");
          }
        } catch {
          // Session validation failed, clear user data
          console.log("Session validation failed, clearing user data");
          setUser(null);
          localStorage.removeItem("user");
        }
      }

      setIsValidating(false);
    };

    checkSession();
  }, []);

  useEffect(() => {
    // Skip during initial validation — checkSession needs localStorage intact to read
    // the stored token and decide whether to call validateSession.
    if (isValidating) return;
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  }, [user, isValidating]);

  useEffect(() => {
    if (viewAsId) {
      localStorage.setItem("viewAsUserId", String(viewAsId));
    } else {
      localStorage.removeItem("viewAsUserId");
    }
  }, [viewAsId]);

  // Show nothing while validating to prevent flash of login page
  if (isValidating) {
    return null;
  }

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
}
